import { computed, defineComponent, h, onUnmounted, ref, unref, watch } from "@vue/composition-api";
import { LiveChatInvitation } from "@scrile/api-provider/dist/api/LivechatsProvider";
import useDoNotDisturb from "@/hooks/useDoNotDisturb";
import useFiles from "@/hooks/useFiles";
import { parseISO } from "@/lib/time";
import useConsume from "@/views/PageProfile/components/StreamController/hooks/useConsume";
import { useProduce } from "@/views/PageProfile/components/StreamController/hooks/useProduce";
import { usePrivateLiveChat } from "../../hooks/usePrivateLiveChat";
import { usePrivateLiveInvitationProducer } from "../../hooks/usePrivateLiveInvitationProducer";
import usePublicLiveChat from "../../hooks/usePublicLiveChat";
import StreamView from "../StreamView";

export default defineComponent({
  name: "StreamOwnerSide",
  props: {
    userId: { type: String, required: true },
  },
  setup(props, { emit }) {
    const { setDoNotDisturb } = useDoNotDisturb();
    const { findFiles } = useFiles();
    const hasManualPreview = ref(false);

    const showLeavingPrivate = ref(false);
    const showGoingPrivate = ref(false);

    const getFileInfo = async () => {
      const files = await findFiles({
        fileIds: [
          {
            subjectId: props.userId,
            type: "livepreview_manual",
          },
        ],
      });
      hasManualPreview.value = files.length > 0;
    };

    const { loading, joinData, userList, clientServerTimeDiff, errorData } = usePublicLiveChat({
      userId: props.userId,
    });

    const {
      loading: privateLoading,
      joinData: privateJoinData,
      errorData: privateErrorData,
      isPrivateLive,
      consumer: privateConsumer,
      checkingPrivateLive,
      clientServerTimeDiff: privateClientServerTimeDiff,
      joinPrivateLive,
      endPrivateLive,
    } = usePrivateLiveChat({ userId: props.userId });

    const { privateLiveInvitations, invitationDecline, invitationApprove } = usePrivateLiveInvitationProducer(
      props.userId
    );

    const getLoggerContext = (): Record<string, any> => ({
      joinData: joinData.value,
      errorData: errorData.value,
      privateErrorData: privateErrorData.value,
    });

    const {
      stream,
      connecting,
      connectingState,
      constraints,
      produceStream,
      stopBroadcasting,
      onChangeDevice,
      reset,
      createLocalStream,
      initStream,
    } = useProduce(getLoggerContext);

    const { usersToConsume, consumeStream, closeAllStreams: closeConsumeStream } = useConsume({
      joinData: privateJoinData,
      getLoggerContext,
    });

    const dateStart = computed(() => {
      const data = isPrivateLive.value ? privateJoinData.value : joinData.value;
      const timeDiff = isPrivateLive.value ? privateClientServerTimeDiff.value : clientServerTimeDiff.value;
      if (!data) {
        return "";
      }
      return new Date(parseISO(data.me.joinTime).getTime() + timeDiff).toISOString();
    });

    const goingPrivate = async () => {
      try {
        showGoingPrivate.value = true;
        if (joinData.value) await stopBroadcasting(joinData.value.token);
        await createLocalStream();
        if (privateJoinData.value) await produceStream(privateJoinData.value.token);
      } finally {
        showGoingPrivate.value = false;
      }
    };

    const onInvitationApprove = async (invitation: LiveChatInvitation) => {
      showGoingPrivate.value = true;
      await invitationApprove(invitation);
      await joinPrivateLive();
    };

    const onClose = async () => {
      if (isPrivateLive.value) {
        reset();
        closeConsumeStream();
        await endPrivateLive();
        await createLocalStream();
        showGoingPrivate.value = false;
        showLeavingPrivate.value = true;
      } else {
        emit("close");
      }
    };

    const onPublicProduce = async () => {
      showLeavingPrivate.value = false;
      if (joinData.value) await produceStream(joinData.value.token);
    };

    watch(joinData, async (joinData, old) => {
      await checkingPrivateLive;
      if (!old && joinData && !isPrivateLive.value) {
        produceStream(joinData.token);
      }
    });

    watch(privateJoinData, (privateJoinData, old) => {
      const consumer = privateJoinData?.users.find(u => u.userId === privateConsumer.value?.id);
      const oldConsumer = old?.users.find(u => u.userId === privateConsumer.value?.id);
      if (!old && privateJoinData) {
        goingPrivate();
      } else if (privateJoinData && !consumer && oldConsumer) {
        onClose();
      }
    });

    watch(privateJoinData, (joinData, old) => {
      if (!joinData) return;
      const producer = joinData.users.find(i => i.userId !== props.userId);
      const oldProducer = old?.users.find(i => i.userId !== props.userId);
      if (producer?.broadcasting && !oldProducer?.broadcasting) {
        consumeStream(producer.id, joinData.token);
      } else if (!producer?.broadcasting && oldProducer?.broadcasting) {
        closeConsumeStream();
      }
    });

    const init = async () => {
      await Promise.all([initStream(), getFileInfo()]);
    };

    init();
    setDoNotDisturb(true);

    onUnmounted(() => {
      setDoNotDisturb(false);
    });

    return () =>
      h(StreamView, {
        props: {
          ownerId: props.userId,
          isProducing: true,
          isOwner: true,
          localStream: unref(stream),
          usersToConsume: unref(usersToConsume),
          connecting: unref(connecting),
          connectingState: unref(connectingState),
          loading: isPrivateLive.value ? unref(privateLoading) : unref(loading),
          userList: unref(userList),
          streamStartedDate: unref(dateStart),
          threadId: joinData.value?.threadId,
          token: joinData.value?.token,
          privateThreadId: privateJoinData.value?.threadId,
          privateToken: privateJoinData.value?.token,
          isPrivateLive: unref(isPrivateLive),
          showLeavingPrivate: unref(showLeavingPrivate),
          showGoingPrivate: unref(showGoingPrivate),
          constraints: unref(constraints),
          privateLiveInvitations: unref(privateLiveInvitations),
          privateConsumer: unref(privateConsumer),
          hasManualPreview: unref(hasManualPreview),
        },
        on: {
          close: onClose,
          changeDevice: onChangeDevice,
          acceptInvitation: onInvitationApprove,
          declineInvitation: invitationDecline,
          publicProduce: onPublicProduce,
        },
      });
  },
});
